import { DASHLET_INTERNAL_MESSAGE } from '../../../app/frontend/js/common';
import React, { createContext, useCallback, useRef } from 'react';
import {
  constant,
  filter,
  forEach,
  get,
  head,
  isEmpty,
  isEqual,
  noop,
} from 'lodash';
import { ISugarApp } from '../mfe.interfaces';
import { shortid } from '../../../app/frontend/js/common/utilities/shortid-adapter';

declare const App: ISugarApp;

export type DashletInternalMessageContextType = {
  sendMessage: (actionName: DASHLET_INTERNAL_MESSAGE, ...args) => void;
  registerListener: (
    actionName: DASHLET_INTERNAL_MESSAGE,
    handler: (...args) => void,
  ) => string;
  deregisterListener: (handlerName: string) => void;
};

export const DashletInternalMessageContext =
  createContext<DashletInternalMessageContextType>({
    sendMessage: noop,
    registerListener: constant(''),
    deregisterListener: noop,
  });

export const DashletInternalMessageProvider = ({
  children,
  channelId = '',
}) => {
  const handlers = useRef({});
  const sendMessage = useCallback(
    (actionName, ...args) => {
      !isEmpty(channelId) && App.events.trigger(channelId, actionName, ...args);
    },
    [channelId],
  );

  const registerListener = useCallback(
    (actionName, handler = noop) => {
      const handlerName = `handler-${shortid.generate()}`;

      handlers.current[handlerName] = (_actionName, ...args) =>
        isEqual(actionName, _actionName) && handler(...args);

      !isEmpty(channelId) &&
        App.events.on(channelId, handlers.current[handlerName]);

      // provide any buffered history, if exists
      sendMessage(
        DASHLET_INTERNAL_MESSAGE.LISTENER_MOUNTED,
        (
          messageBuffer: Parameters<
            DashletInternalMessageContextType['registerListener']
          >[],
        ) => {
          const preMountHistory = filter(messageBuffer, message =>
            isEqual(head(message), actionName),
          );
          forEach(preMountHistory, ([_actionName, ...args]) =>
            handler(...args),
          );
        },
      );

      return handlerName;
    },
    [channelId, sendMessage],
  );

  const deregisterListener = useCallback(
    handlerName => {
      !isEmpty(channelId) &&
        get(handlers.current, handlerName) &&
        App.events.off(channelId, handlers.current[handlerName]);
    },
    [channelId],
  );

  return (
    <DashletInternalMessageContext.Provider
      value={{ sendMessage, registerListener, deregisterListener }}
    >
      {children}
    </DashletInternalMessageContext.Provider>
  );
};
